import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiFehler, hole, schicke } from '../lib/api'
import { datumKurz } from '../lib/datum'
import { mitgliedsFarbe } from '../session/logic'
import { useDarf, useSitzung } from '../session/SessionProvider'
import { useFab } from '../shell/FabSlot'
import {
  Avatar,
  Button,
  Empty,
  IconButton,
  ListItem,
  Sheet,
  TextField,
  useConfirm,
  useSnackbar,
} from '../ui'

interface Kontakt {
  id: string
  displayName: string
  phone: string | null
  email: string | null
  note: string | null
  birthDate: string | null
  visibility: string
}

const LEER = { displayName: '', phone: '', email: '', note: '', birthDate: '' }

/**
 * Kontakte — das Adressbuch des Haushalts.
 *
 * Telefonnummer und E-Mail sind echte `tel:`- und `mailto:`-Verweise: der
 * häufigste Grund, hier zu landen, ist anrufen zu wollen.
 */
export function Kontakte() {
  const qc = useQueryClient()
  const { abfrage } = useSitzung()
  const zeigeMeldung = useSnackbar()
  const bestaetige = useConfirm()
  const preview = Boolean(abfrage.data && !abfrage.data.setupRequired && abfrage.data.preview)
  const darfAendern = useDarf('contacts.edit') && !preview
  const darfArchivieren = useDarf('contacts.delete') && !preview

  const [suche, setSuche] = useState('')
  const [blattOffen, setBlattOffen] = useState(false)
  const [formular, setFormular] = useState({ ...LEER })
  const [bearbeitet, setBearbeitet] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const liste = useQuery({
    queryKey: ['contacts', 'list'],
    queryFn: () => hole<{ contacts: Kontakt[] }>('/api/contacts'),
  })
  const alle = liste.data?.contacts ?? []
  const gesucht = suche.trim().toLocaleLowerCase('de-DE')
  const sichtbar =
    gesucht.length === 0
      ? alle
      : alle.filter((k) =>
          [k.displayName, k.phone, k.email, k.note]
            .filter((x): x is string => typeof x === 'string')
            .some((x) => x.toLocaleLowerCase('de-DE').includes(gesucht)),
        )

  const aktualisiere = async () => {
    await qc.invalidateQueries({ queryKey: ['contacts'] })
  }

  const leerOderWert = (wert: string) => (wert.trim() === '' ? null : wert.trim())

  const speichern = useMutation({
    mutationFn: () => {
      const rumpf = {
        displayName: formular.displayName,
        phone: leerOderWert(formular.phone),
        email: leerOderWert(formular.email),
        note: leerOderWert(formular.note),
        birthDate: leerOderWert(formular.birthDate),
      }
      return bearbeitet === null
        ? schicke<{ contact: Kontakt }>('POST', '/api/contacts', rumpf)
        : schicke<{ contact: Kontakt }>('PATCH', `/api/contacts/${bearbeitet}`, rumpf)
    },
    onSuccess: async () => {
      setBlattOffen(false)
      setBearbeitet(null)
      setFormular({ ...LEER })
      setFehler(null)
      await aktualisiere()
      zeigeMeldung('Gespeichert.')
    },
    onError: (e: ApiFehler) => setFehler(e.message),
  })

  const archivieren = useMutation({
    mutationFn: (id: string) => schicke('DELETE', `/api/contacts/${id}`),
    onSuccess: async () => {
      await aktualisiere()
      zeigeMeldung('Kontakt archiviert.')
    },
    onError: (e: ApiFehler) => {
      zeigeMeldung(e.message)
    },
  })

  const oeffneNeu = () => {
    setBearbeitet(null)
    setFormular({ ...LEER })
    setFehler(null)
    setBlattOffen(true)
  }

  useFab(darfAendern ? { icon: 'plus', label: 'Kontakt anlegen', onClick: oeffneNeu } : null)

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Kontakte</h1>
          <p>Das Adressbuch des Haushalts.</p>
        </div>
      </div>

      {alle.length > 0 ? (
        <TextField id="kontakt-suche" label="Suchen" wert={suche} onWert={setSuche} type="search" />
      ) : null}

      {liste.isPending ? <p className="unterzeile">Wird geladen …</p> : null}

      {!liste.isPending && alle.length === 0 ? (
        <Empty
          titel="Noch keine Kontakte"
          text="Kinderarzt, Schule, Oma — alles, was die Familie öfter braucht."
        />
      ) : null}

      {alle.length > 0 && sichtbar.length === 0 ? (
        <Empty titel="Nichts gefunden" text={`Kein Kontakt passt zu „${suche.trim()}".`} />
      ) : null}

      {sichtbar.length > 0 ? (
        <ul className="liste liste--gruppe">
          {sichtbar.map((k) => (
            <ListItem
              key={k.id}
              fuehrend={<Avatar name={k.displayName} farbe={mitgliedsFarbe(null, k.id)} />}
              titel={k.displayName}
              unterzeile={
                <span className="kontakt-zeilen">
                  {k.phone ? (
                    <a className="kontakt-verweis" href={`tel:${k.phone.replace(/\s/g, '')}`}>
                      {k.phone}
                    </a>
                  ) : null}
                  {k.email ? (
                    <a className="kontakt-verweis" href={`mailto:${k.email}`}>
                      {k.email}
                    </a>
                  ) : null}
                  {k.birthDate ? <span>Geburtstag {datumKurz(k.birthDate)}</span> : null}
                  {k.note ? <span>{k.note}</span> : null}
                </span>
              }
              folgend={
                <span className="eintrag-aktionen">
                  {darfAendern ? (
                    <IconButton
                      name="edit"
                      label={`${k.displayName} bearbeiten`}
                      onClick={() => {
                        setBearbeitet(k.id)
                        setFormular({
                          displayName: k.displayName,
                          phone: k.phone ?? '',
                          email: k.email ?? '',
                          note: k.note ?? '',
                          birthDate: k.birthDate ?? '',
                        })
                        setFehler(null)
                        setBlattOffen(true)
                      }}
                    />
                  ) : null}
                  {darfArchivieren ? (
                    <IconButton
                      name="trash"
                      label={`${k.displayName} archivieren`}
                      onClick={() => {
                        void bestaetige({
                          titel: 'Kontakt archivieren?',
                          text: `${k.displayName} archivieren?`,
                          bestaetigen: 'Archivieren',
                          gefahr: true,
                        }).then((bestaetigt) => {
                          if (bestaetigt) archivieren.mutate(k.id)
                        })
                      }}
                    />
                  ) : null}
                </span>
              }
            />
          ))}
        </ul>
      ) : null}

      <Sheet
        offen={blattOffen}
        titel={bearbeitet === null ? 'Neuer Kontakt' : 'Kontakt bearbeiten'}
        onSchliessen={() => setBlattOffen(false)}
      >
        <form
          className="stack-4"
          onSubmit={(e) => {
            e.preventDefault()
            speichern.mutate()
          }}
        >
          <TextField
            id="kontakt-name"
            label="Name"
            wert={formular.displayName}
            onWert={(w) => setFormular((f) => ({ ...f, displayName: w }))}
          />
          <TextField
            id="kontakt-telefon"
            label="Telefon (optional)"
            type="tel"
            wert={formular.phone}
            onWert={(w) => setFormular((f) => ({ ...f, phone: w }))}
          />
          <TextField
            id="kontakt-mail"
            label="E-Mail (optional)"
            type="email"
            wert={formular.email}
            onWert={(w) => setFormular((f) => ({ ...f, email: w }))}
          />
          <TextField
            id="kontakt-geburtstag"
            label="Geburtstag (optional)"
            type="date"
            wert={formular.birthDate}
            onWert={(w) => setFormular((f) => ({ ...f, birthDate: w }))}
          />
          <TextField
            id="kontakt-notiz"
            label="Notiz (optional)"
            wert={formular.note}
            onWert={(w) => setFormular((f) => ({ ...f, note: w }))}
          />
          {/* ⚠️ Frei über den Knöpfen, nicht an einem Feld: der Server nennt
              kein Feld, also darf die Oberfläche auch keines behaupten. Zuvor
              hing die Meldung fest am E-Mail-Feld - eine Meldung über die
              Telefonnummer markierte damit die E-Mail als ungültig. */}
          {fehler ? (
            <p className="fehler" role="alert">
              {fehler}
            </p>
          ) : null}
          <div className="blatt__fuss-inline">
            <Button variant="text" onClick={() => setBlattOffen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" laeuft={speichern.isPending}>
              Speichern
            </Button>
          </div>
        </form>
      </Sheet>
    </>
  )
}
